import { ToastContainerDirective } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SnCameraStatus, CameraOwnership } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { GlobalContextMap } from '@lib/data-dog-rum/global-context-map';
import { GoogleMap } from '@lib/geo/google/map';

const INNER_LAYERS_STORE_KEY = 'selectedInnerLayers';
const DYNAMIC_LAYERS_STORE_KEY = 'selectedDynamicLayers';
const CAMERA_STATUS_FILTER_KEY = 'selectedCameraStatuses';
const LPR_MAKE_FILTER_KEY = 'selectedLprMakes';
const DISABLED_CLUSTER_OFF_FILTER_STORE_KEY = 'disabledClusterFilters';
const ROOM_CLEARING_STORE_KEY = 'isRoomClearingEnabled';

const DEFAULT_CAMERA_STATUS_ARRAY = [SnCameraStatus.Up, SnCameraStatus.Down, SnCameraStatus.Unmanaged, CameraOwnership.MyCameras, CameraOwnership.SharedWithMe];

@Injectable()
export class MapStoreService {
  get disabledClusterFilters(): string[] {
    return Array.from(this._disabledClusterFilters);
  }

  get selectedInnerLayers(): string[] {
    return Array.from(this._selectedInnerLayers);
  }

  get selectedDynamicLayers(): string[] {
    return Array.from(this._selectedDynamicLayers);
  }

  get disabledAlarmReasonFilters(): string[] {
    return Array.from(this._disabledAlarmReasonFilters);
  }

  get enabledAlarmReasonFilters(): string[] {
    return Array.from(this._enabledAlarmReasonFilters);
  }

  get disabledAlarmTypeFilters(): string[] {
    return Array.from(this._disabledAlarmTypeFilters);
  }

  get isRoomClearingEnabled(): boolean {
    return this._isRoomClearingEnabled;
  }

  get isLprMigrated(): boolean {
    return this._currentUser.settings?.mapSetting?.isLprMigrated;
  }

  get displayCameraLabels(): boolean {
    return this._displayCameraLabels;
  }

  get displayCameraLabelsOnlyZoomed(): boolean {
    return this._displayCameraLabelsOnlyZoomed;
  }

  public cameraTypeArray: string[];
  public lprCameraMakeArray: string[];

  public clusterTypeArray: { Clustering: string; checked: boolean }[];

  public toastContainer: ToastContainerDirective;
  public map: GoogleMap;

  public defaultAlarmReasonFilters = [];
  public defaultAlarmTypeFilters = ['SoundThinking', 'Panic App'];
  public defaultClusterTypeFilters = ['Alerts', 'Sex Offenders', 'fususTips', 'Cameras'];

  private _isRoomClearingEnabled: boolean = false;
  private _displayCameraLabels: boolean = false;
  private _displayCameraLabelsOnlyZoomed: boolean = true;
  private _alarmReasons = [];
  private readonly _destroy$ = new Subject<void>();

  private readonly _selectedInnerLayers = new Set<string>();
  private readonly _selectedDynamicLayers = new Set<string>();
  private readonly _disabledAlarmReasonFilters = new Set<string>();
  private readonly _enabledAlarmReasonFilters = new Set<string>();
  private readonly _disabledAlarmTypeFilters = new Set<string>();
  private readonly _disabledClusterFilters = new Set<string>();

  constructor(
    private readonly _currentUser: SnCurrentUser,
    private readonly _datadogService: FsDataDogRumService,
  ) {
    this._getAlarmReasonAndTypeFilters();
    this._getInnerLayersFromStore();
    this._getDynamicLayersFromStore();
    this._initCameraStatus();
    this._initClusterStatus();
    this._initLprCameraMakes();
    this._getRoomClearingEnabled();
    this._initCameraLabelSettings();
    this._updateSettings();
  }

  public onMapLoaded(map: GoogleMap): void {
    this.map = map;
  }

  public addDynamicLayer(layer: string): void {
    if (this._selectedDynamicLayers.has(layer)) {
      return;
    }

    this._selectedDynamicLayers.add(layer);
    this._updateSettings();
  }

  public deleteDynamicLayer(layer: string): void {
    if (!this._selectedDynamicLayers.has(layer)) {
      return;
    }

    this._selectedDynamicLayers.delete(layer);
    this._updateSettings();
  }

  public isInnerLayerSelected(layer: string): boolean {
    return this._selectedInnerLayers.has(layer);
  }

  public addInnerLayer(layer: string): void {
    if (this._selectedInnerLayers.has(layer)) {
      return;
    }

    this._selectedInnerLayers.add(layer);
    this._updateSettings();
  }

  public deleteInnerLayer(layer: string): void {
    if (!this._selectedInnerLayers.has(layer)) {
      return;
    }

    this._selectedInnerLayers.delete(layer);
    this._updateSettings();
  }

  public alarmsReasonsFilteredOff(): string[] {
    return this._alarmReasons
      ?.filter((v) => {
        return (
          !!v &&
          !this.enabledAlarmReasonFilters.some((e) => {
            return v?.toLowerCase().trim() === e?.toString().toLowerCase().trim();
          })
        );
      })
      .map((v) => v.toLowerCase());
  }

  public alarmsReasonsFilteredOn(): string[] {
    return this.enabledAlarmReasonFilters.map((f) => f.toLowerCase());
  }

  public alarmsTypeFilteredOff(): string[] {
    return this.disabledAlarmTypeFilters;
  }

  public setAlarmReasons(reasons: string[]): void {
    this._getAlarmReasonAndTypeFilters();
    this._alarmReasons = reasons;
  }

  public setEnabledAlarmReasonFilters(filters: string[], layerSetting?: any): void {
    this._enabledAlarmReasonFilters.clear();
    filters.forEach((filter) => this._enabledAlarmReasonFilters.add(filter));
    const currentLayerSetting = layerSetting || {};
    this._currentUser
      .updateSettings({
        ...this._currentUser.settings,
        layerSetting: {
          ...this._currentUser.settings.layerSetting,
          ...currentLayerSetting,
        },
        mapSetting: {
          ...this._currentUser.settings.mapSetting,
          enabledAlertReasons: [...this.enabledAlarmReasonFilters],
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((settings) => {
        this._currentUser.settings = settings;
      });
  }

  public setDisabledAlarmTypeFilters(filters: string[], layerSetting?: any): void {
    this._disabledAlarmTypeFilters.clear();
    filters.forEach((filter) => this._disabledAlarmTypeFilters.add(filter));
    const currentLayerSetting = layerSetting || {};
    this._currentUser
      .updateSettings({
        ...this._currentUser.settings,
        layerSetting: {
          ...this._currentUser.settings.layerSetting,
          ...currentLayerSetting,
        },
        mapSetting: {
          ...this._currentUser.settings.mapSetting,
          disabledAlertTypes: [...this.disabledAlarmTypeFilters],
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((settings) => {
        this._currentUser.settings = settings;
      });
  }

  public setCameraStatus(cameras: string[], ind: any): string[] {
    const index = cameras.indexOf(ind);
    if (index > -1) {
      cameras.splice(index, 1);
    } else {
      cameras.push(ind);
    }
    this._updateSettings();
    return cameras;
  }

  public setLprMake(makes: string[], ind: any): string[] {
    const index = makes.indexOf(ind);
    if (index > -1) {
      makes.splice(index, 1);
    } else {
      makes.push(ind);
    }
    this._updateSettings();
    return makes;
  }

  public toggleCluster(layer: string, checked: boolean): void {
    if (checked) {
      if (!this._disabledClusterFilters.has(layer)) {
        return;
      }
      this._disabledClusterFilters.delete(layer);
    } else {
      if (this._disabledClusterFilters.has(layer)) {
        return;
      }
      this._disabledClusterFilters.add(layer);
    }
    this._updateSettings();
  }

  public isClustered(layer: string): boolean {
    return !this._disabledClusterFilters.has(layer);
  }

  public setRoomClearingStatus(on: boolean): void {
    this._isRoomClearingEnabled = on;
    this._updateSettings();
  }

  public setDisplayCameraLabels(enabled: boolean): void {
    this._displayCameraLabels = enabled;
    this._updateSettings();
  }

  public setDisplayCameraLabelsOnlyZoomed(enabled: boolean): void {
    this._displayCameraLabelsOnlyZoomed = enabled;
    this._updateSettings();
  }

  public updateSettings(): void {
    this._updateSettings();
  }

  private _initCameraStatus(): void {
    const statusesString = localStorage.getItem(CAMERA_STATUS_FILTER_KEY);
    if (!this._currentUser.settings?.mapSetting?.selectedCameraStatuses) {
      if (statusesString) {
        const statuses: string[] = JSON.parse(statusesString);
        this.cameraTypeArray = statuses;
      } else {
        this.cameraTypeArray = DEFAULT_CAMERA_STATUS_ARRAY;
      }
    } else {
      const savedLayers = this._getMapSetting(CAMERA_STATUS_FILTER_KEY);
      this.cameraTypeArray = savedLayers || [];
    }
  }

  private _initLprCameraMakes(): void {
    if (!this.isLprMigrated) {
      this._currentUser.settings.mapSetting.selectedLprMakes = [];
    }
    const lprFiltersString = localStorage.getItem(LPR_MAKE_FILTER_KEY);
    if (!this._currentUser.settings?.mapSetting?.selectedLprMakes) {
      if (lprFiltersString) {
        const makes: string[] = JSON.parse(lprFiltersString);
        this.lprCameraMakeArray = makes;
      } else {
        this.lprCameraMakeArray = [
          'ALTUMINT',
          'BLUE_LINE',
          'EAGLE_EYE_NETWORKS',
          'ELSAG',
          'EPIC_IO',
          'FLOCK_SAFETY',
          'GENETEC',
          'GENETEC_SHARP3',
          'GENETEC_SHARP5',
          'GEOVISION',
          'FUSUS',
          'INSIGHT_LPR',
          'LOGIQ',
          'MILESTONE',
          'MINUTEMAN',
          'NDI',
          'OPENALPR',
          'REKOR',
          'RHOMBUS',
          'VAXTOR',
          'VERKADA',
          'VIGILANT',
          'WATCHMAN',
          'WOLFCOM',
        ];
      }
    } else {
      const savedMakes = this._getMapSetting(LPR_MAKE_FILTER_KEY);
      this.lprCameraMakeArray = savedMakes || [];
    }
  }

  private _initClusterStatus(): void {
    const disabledClusterFiltersString = localStorage.getItem(
      DISABLED_CLUSTER_OFF_FILTER_STORE_KEY,
    );
    if (!this._currentUser.settings?.mapSetting?.disabledClusterFilters) {
      if (disabledClusterFiltersString) {
        const disabledFilters: string[] = JSON.parse(disabledClusterFiltersString);
        disabledFilters.forEach((layer) => this._disabledClusterFilters.add(layer));
      }
    } else {
      const savedFilters = this._getMapSetting(DISABLED_CLUSTER_OFF_FILTER_STORE_KEY);
      savedFilters.forEach((layer) => this._disabledClusterFilters.add(layer));
    }
  }

  private _getInnerLayersFromStore(): void {
    const layersString = localStorage.getItem(INNER_LAYERS_STORE_KEY);
    if (!this._currentUser.settings?.mapSetting?.selectedInnerLayers) {
      if (layersString) {
        const layers: string[] = JSON.parse(layersString);
        layers.forEach((layer) => this._selectedInnerLayers.add(layer));
      } else {
        ['users', 'alarms', 'locations', 'cameras', 'tips', 'lprCameras', 'panoramas'].forEach(
          (layer) => this._selectedInnerLayers.add(layer),
        );
      }
    } else {
      const savedLayers = this._getMapSetting(INNER_LAYERS_STORE_KEY);
      if (savedLayers?.length) {
        savedLayers.forEach((layer) => this._selectedInnerLayers.add(layer));
      }
    }
  }

  private _getDynamicLayersFromStore(): void {
    const layersString = localStorage.getItem(DYNAMIC_LAYERS_STORE_KEY);
    if (!this._currentUser.settings?.mapSetting?.selectedDynamicLayers) {
      if (layersString) {
        const layers: string[] = JSON.parse(layersString);
        layers.forEach((layer) => this._selectedDynamicLayers.add(layer));
      }
    } else {
      const savedLayers = this._getMapSetting(DYNAMIC_LAYERS_STORE_KEY);
      if (savedLayers?.length) {
        savedLayers.forEach((layer) => this._selectedDynamicLayers.add(layer));
      }
    }
  }

  private _getAlarmReasonAndTypeFilters(): void {
    const enabledReasons = this._currentUser.settings?.mapSetting?.enabledAlertReasons;
    const disabledTypes = this._currentUser.settings?.mapSetting?.disabledAlertTypes;
    if (enabledReasons?.length) {
      enabledReasons.forEach((reason) => {
        this._enabledAlarmReasonFilters.add(reason);
      });
    }
    if (disabledTypes?.length) {
      disabledTypes.forEach((type) => {
        this._disabledAlarmTypeFilters.add(type);
      });
    }
  }

  private _getRoomClearingEnabled(): void {
    const isRoomClearingEnabledString = localStorage.getItem(ROOM_CLEARING_STORE_KEY);
    if (!this._currentUser.settings?.mapSetting?.isRoomClearingEnabled) {
      if (isRoomClearingEnabledString) {
        this._isRoomClearingEnabled = isRoomClearingEnabledString === 'true' ? true : false;
      }
    } else {
      this._isRoomClearingEnabled = this._getMapSetting(ROOM_CLEARING_STORE_KEY);
    }
  }

  private _initCameraLabelSettings(): void {
    const displayLabels = this._currentUser.settings?.mapSetting?.displayCameraLabels;
    const displayLabelsOnlyZoomed =
      this._currentUser.settings?.mapSetting?.displayCameraLabelsOnlyZoomed;

    if (displayLabels !== undefined) {
      this._displayCameraLabels = displayLabels;
    }

    if (displayLabelsOnlyZoomed !== undefined) {
      this._displayCameraLabelsOnlyZoomed = displayLabelsOnlyZoomed;
    }
  }

  private _getMapSetting(key: string): any {
    return this._currentUser.settings.mapSetting[key];
  }

  private _getLogData(): GlobalContextMap['map_settings'] {
    return {
      selectedInnerLayers: Array.from(this._selectedInnerLayers),
      selectedDynamicLayers: Array.from(this._selectedDynamicLayers),
    };
  }

  private _updateSettings(): void {
    if (!this._currentUser.settings) {
      return;
    }

    const updatedMapSettings = {
      selectedInnerLayers: Array.from(this._selectedInnerLayers),
      selectedDynamicLayers: Array.from(this._selectedDynamicLayers),
      selectedCameraStatuses: this.cameraTypeArray,
      selectedLprMakes: this.lprCameraMakeArray,
      disabledClusterFilters: Array.from(this._disabledClusterFilters),
      isRoomClearingEnabled: this.isRoomClearingEnabled,
      isLprMigrated: true,
      displayCameraLabels: this._displayCameraLabels,
      displayCameraLabelsOnlyZoomed: this._displayCameraLabelsOnlyZoomed,
    };

    this._currentUser
      .updateSettings({
        ...this._currentUser.settings,
        mapSetting: {
          ...this._currentUser.settings.mapSetting,
          ...updatedMapSettings,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe();
    this._datadogService.setGlobalContextProperty('map_settings', this._getLogData());
  }
}
